// Real screenshots of each product, not stock art.
import medicMinderImage from '../images/work/medicminder-app.jpg'
import archiMlImage from '../images/work/archiml-app.jpg'
import medLocatorImage from '../images/work/medlocator-site.jpg'
import medicalTourismImage from '../images/work/medical-tourism-site.jpg'
import neteviaImage from '../images/netevia.png'
import knowledgeAssistantImage from '../images/work/knowledge-assistant.svg' // illustration: internal app under NDA
// Hover clips (videos/work): looping motion graphics, one per project, in the
// site's colours. To swap in AI-generated footage, see docs/ai-video-prompts.md.
import medicMinderClip from '../videos/work/medicminder.mp4'
import archiMlClip from '../videos/work/archiml.mp4'
import medLocatorClip from '../videos/work/medlocator.mp4'
import medicalTourismClip from '../videos/work/medical-tourism.mp4'
import neteviaClip from '../videos/work/netevia.mp4'

/**
 * Every project the site shows, in one place.
 *
 * ── Adding a new project ────────────────────────────────────────────────
 *  1. Put the image in `src/images/` and import it at the top of this file.
 *  2. Copy one of the entries below and fill it in.
 *  3. Set `featured: true` for the ones you want in the homepage
 *     showcase strip (any number works; all cards are one size).
 * That is the whole job — the homepage showcase and the /projects grid both
 * read from here, so a project can never appear in one and be missing from
 * the other.
 *
 * Fields
 *  img          image import (landscape reads best, roughly 3:2)
 *  video        optional clip — plays on hover, see src/videos/README.md
 *  stats        up to two { value, labelKey } figures shown on the card on
 *               hover — copy them from the project's case study results
 *               (data/caseStudies.js), never new claims
 *  title        shown as-is, not translated
 *  tagKey       translation key for the badge, e.g. 'workTag1'
 *  textKey      translation key for the description, e.g. 'workText1'
 *  link         internal route ('/products/x') or a full external URL
 *  category     'ML' | 'Application' | 'Web' — drives the /projects filter
 *  featured     true to include it in the homepage showcase
 */
const projects = [
  {
    img: medicMinderImage,
    video: medicMinderClip,
    stats: [{ value: '98%', labelKey: 'workStat1a' }, { value: '8–12×', labelKey: 'workStat1b' }],
    title: 'MedicMinder',
    tagKey: 'workTag1',
    textKey: 'workText1',
    link: '/products/medicminder',
    category: 'Application',
    featured: true,
  },
  {
    img: archiMlImage,
    video: archiMlClip,
    stats: [{ value: '7', labelKey: 'workStat2a' }, { value: '2', labelKey: 'workStat2b' }],
    title: 'ArchiML',
    tagKey: 'workTag2',
    textKey: 'workText2',
    link: '/products/archiml',
    category: 'ML',
    featured: true,
  },
  {
    img: medLocatorImage,
    video: medLocatorClip,
    stats: [{ value: '84', labelKey: 'workStat3a' }, { value: '4', labelKey: 'workStat3b' }],
    title: 'MedLocator',
    tagKey: 'workTag3',
    textKey: 'workText3',
    link: '/projects/medlocator',
    category: 'Web',
    featured: true,
  },
  {
    img: medicalTourismImage,
    video: medicalTourismClip,
    stats: [{ value: '678', labelKey: 'workStat4a' }, { value: '65', labelKey: 'workStat4b' }],
    title: 'Medical Tourism',
    tagKey: 'workTag4',
    textKey: 'workText4',
    link: '/projects/medical-tourism',
    category: 'Web',
    featured: true,
  },
  {
    img: neteviaImage,
    video: neteviaClip,
    stats: [{ value: '40,000+', labelKey: 'workStat5a' }, { value: '$15B', labelKey: 'workStat5b' }],
    title: 'Netevia',
    tagKey: 'workTag5',
    textKey: 'workText5',
    link: '/projects/netevia',
    category: 'Web',
    featured: true,
  },
  {
    img: knowledgeAssistantImage,
    title: 'Medical Knowledge Assistant',
    tagKey: 'workTag6',
    textKey: 'workText6',
    link: '/projects/knowledge-assistant',
    category: 'ML',
    featured: true,
  },
]

// Resolves the translation keys against the active language.
export const resolveProjects = (translations, { featuredOnly = false } = {}) =>
  projects
    .filter((project) => (featuredOnly ? project.featured : true))
    .map((project) => ({
      ...project,
      tag: translations[project.tagKey] || '',
      text: translations[project.textKey] || '',
      stats: (project.stats || []).map((stat) => ({ value: stat.value, label: translations[stat.labelKey] || '' })),
    }))

// Filter categories shown on /projects, derived so a new category needs no
// second edit anywhere.
export const projectCategories = ['All', ...Array.from(new Set(projects.map((p) => p.category)))]

export default projects
